import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { SPECIALITIES } from "@/constants";
import { getGps } from "@/lib/format";
import { MagnifyingGlass, Plus, User, Storefront, Truck } from "@phosphor-icons/react";

const ICONS = { doctor: User, retailer: Storefront, stockist: Truck };

const FIELDS = {
  doctor: [
    { k: "name", req: true }, { k: "speciality", type: "speciality" },
    { k: "clinic" }, { k: "mobile" }, { k: "city" }, { k: "area" },
  ],
  retailer: [
    { k: "name", req: true }, { k: "contact_person" }, { k: "mobile" },
    { k: "city" }, { k: "area" }, { k: "gst_number" },
  ],
  stockist: [
    { k: "business_name", req: true }, { k: "contact_person" }, { k: "mobile" },
    { k: "city" }, { k: "area" }, { k: "gst_number" },
  ],
};

export function ContactPicker({ ctype, onSelect, areas }) {
  const { lang, t } = useLang();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({});
  const [dups, setDups] = useState(null);
  const [specs, setSpecs] = useState(SPECIALITIES);
  const Icon = ICONS[ctype];

  const load = () => api.get(`/contacts/${ctype}`, { params: { q } }).then(({ data }) => setList(data));
  useEffect(() => { load(); api.get("/options", { params: { type: "speciality" } }).then(({ data }) => setSpecs(data.map((o) => o.label))).catch(() => {}); /* eslint-disable-next-line */ }, [ctype]);
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); /* eslint-disable-next-line */ }, [q]);

  const nameOf = (c) => c.name || c.business_name;

  const doCreate = async (force) => {
    if (!force) {
      const { data } = await api.post(`/contacts/${ctype}/check-duplicate`, form);
      if (data.duplicates.length) { setDups(data.duplicates); return; }
    }
    const gps = await getGps();
    const { data } = await api.post(`/contacts/${ctype}`, { ...form, gps });
    setAdding(false); setForm({}); setDups(null);
    onSelect(data);
  };

  if (adding) {
    return (
      <div className="space-y-3" data-testid="contact-add-form">
        <div className="flex items-center justify-between">
          <h3 className="font-head font-semibold text-lg">{t("add_new")}</h3>
          <button onClick={() => { setAdding(false); setDups(null); }} className="text-sm text-slate-500" data-testid="contact-add-cancel">{t("cancel")}</button>
        </div>
        {FIELDS[ctype].map((f) => (
          <div key={f.k}>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{f.k.replace("_", " ")}{f.req ? " *" : ""}</label>
            {f.type === "speciality" ? (
              <select
                data-testid={`contact-field-${f.k}`}
                className="mt-1 w-full min-h-[48px] rounded-lg border border-slate-200 px-3 bg-white"
                value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
              >
                <option value="">--</option>
                {specs.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            ) : f.k === "area" ? (
              <select
                data-testid={`contact-field-${f.k}`}
                className="mt-1 w-full min-h-[48px] rounded-lg border border-slate-200 px-3 bg-white"
                value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
              >
                <option value="">--</option>
                {(areas || []).map((a) => <option key={a.id} value={a.name}>{a.name}</option>)}
              </select>
            ) : (
              <input
                data-testid={`contact-field-${f.k}`}
                className="mt-1 w-full min-h-[48px] rounded-lg border border-slate-200 px-3"
                value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
              />
            )}
          </div>
        ))}
        {dups && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="duplicate-warning">
            <p className="text-sm font-semibold text-amber-800">{t("duplicate_warning")}</p>
            {dups.map((d) => (
              <button key={d.id} onClick={() => { setAdding(false); onSelect(d); }} className="block w-full text-left text-sm rounded-md bg-white border border-amber-200 px-3 py-2" data-testid={`dup-${d.id}`}>
                {nameOf(d)} · {d.area}
              </button>
            ))}
            <div className="flex gap-2">
              <button onClick={() => doCreate(true)} className="flex-1 rounded-lg bg-slate-800 text-white py-2 text-sm font-semibold" data-testid="dup-continue">{t("continue_creating")}</button>
            </div>
          </div>
        )}
        <button
          onClick={() => doCreate(false)}
          disabled={!form[FIELDS[ctype][0].k]}
          data-testid="contact-save-btn"
          className="w-full min-h-[48px] rounded-lg bg-emerald-600 text-white font-semibold disabled:opacity-40"
        >{t("save")}</button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3">
        <MagnifyingGlass size={18} className="text-slate-400" />
        <input
          data-testid="contact-search"
          value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")}
          className="flex-1 min-h-[48px] outline-none bg-transparent"
        />
      </div>
      <button
        onClick={() => setAdding(true)}
        data-testid="contact-add-new"
        className="w-full min-h-[48px] rounded-lg border-2 border-dashed border-emerald-400 text-emerald-600 font-semibold flex items-center justify-center gap-2"
      ><Plus size={18} weight="bold" /> {t("add_new")}</button>
      <div className="space-y-2 max-h-[46vh] overflow-y-auto">
        {list.map((c) => (
          <button
            key={c.id}
            data-testid={`contact-item-${c.id}`}
            onClick={() => onSelect(c)}
            className="w-full flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 text-left active:scale-[0.99]"
          >
            <span className="h-10 w-10 rounded-full bg-slate-100 grid place-items-center text-slate-500"><Icon size={20} /></span>
            <span className="flex-1 min-w-0">
              <span className="block font-semibold text-slate-800 truncate">{nameOf(c)}</span>
              <span className="block text-xs text-slate-500 truncate">
                {[c.speciality, c.clinic, c.area].filter(Boolean).join(" · ")}
              </span>
            </span>
            {c.last_visit && <span className="text-[10px] text-slate-400">{c.last_visit}</span>}
          </button>
        ))}
        {list.length === 0 && <p className="text-center text-sm text-slate-400 py-6">{t("no_data")}</p>}
      </div>
    </div>
  );
}
